import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { previewTrustScore } from '../services/api';
import { publishedBandStyle } from '../services/trustBands';
import {
  Dial, FactorRow, DeductionRow, SectionLabel,
  PANEL_STYLE, factorName, evidenceLabel,
} from '../components/v2/trust/trustScoreParts';

/**
 * Score an agent — the first thing a stranger does with this product.
 *
 * Journey 1, steps 2 and 3. Somebody who has never seen Prove7 pastes what their agent is and
 * gets a real score from the real engine on the published method. No account, and nothing is
 * written anywhere: {@code POST /api/v1/trust/scores/preview} computes and answers, and that is
 * the whole transaction.
 *
 * <h2>Why a paste box and not a URL field</h2>
 *
 * A URL field would make our server fetch whatever address a stranger types, which is the
 * outbound-request problem, and it would put it on the critical path of the one page we most
 * want to ship. Taking the content directly removes it entirely. Fetch-by-URL can come later,
 * for signed-in customers, behind the guard.
 *
 * <h2>Why the thin result is shown rather than dressed up</h2>
 *
 * A pasted manifest is a declaration — nothing in it has been observed. The honest answer is a
 * design-time score with the confidence a declaration earns, and saying so plainly is both the
 * truth and the most persuasive argument for connecting the agent that we have. Hiding the
 * thinness would be the worse pitch as well as the dishonest one.
 */

const EXAMPLE = `{
  "name": "claims-triage-prod",
  "auth": "api_key",
  "tools": ["crm.read", "crm.write", "email.send"],
  "data_classes": ["internal", "pii"]
}`;

export function ScoreAnAgentPage() {
  const navigate = useNavigate();
  const [manifest, setManifest] = useState('');
  const [state, setState] = useState('idle');   // idle | scoring | ready | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const style = useMemo(() => publishedBandStyle(result?.score), [result?.score]);
  /* The scorecard leads with the heaviest few, which is what DesignTimeScoringService.deductions
   * says it is ordered for. But the full list accounts for the entire distance from a hundred, so
   * showing three of four silently leaves the arithmetic short - and TrustScoreDeduction's own
   * doc warns that a ladder which does not add up invites the reader to conclude there is a
   * hidden term. The count says which it is. */
  const allDeductions = Array.isArray(result?.deductions) ? result.deductions : [];
  const deductions = allDeductions.slice(0, 3);
  const deductionLabel = allDeductions.length > deductions.length
    ? `What lowered this score \u00b7 top ${deductions.length} of ${allDeductions.length}`
    : 'What lowered this score';
  const factors = Array.isArray(result?.factors) ? result.factors : [];

  const score = useCallback(async () => {
    const text = manifest.trim();
    if (!text) { setError('Paste an agent manifest to score it.'); setState('error'); return; }

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      // Caught here rather than sent on: the server would answer the same thing a round trip
      // later, and a JSON slip is the most likely thing to go wrong on this page.
      setError('That is not valid JSON. Check for a trailing comma or a missing quote.');
      setState('error');
      return;
    }

    setState('scoring');
    setError(null);
    const r = await previewTrustScore(parsed);
    if (r?.ok === false) {
      setError(r.error || 'Could not score that. Try again in a moment.');
      setState('error');
      return;
    }
    setResult(r?.data || r);
    setState('ready');
  }, [manifest]);

  const again = () => { setResult(null); setState('idle'); setError(null); };

  return (
    <div className="min-h-screen bg-v2-page text-v2-ink">
      <div className="max-w-[860px] mx-auto p-4 sm:p-8">
        <header className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} style={{ color: 'var(--v2-text-muted)' }} />
          <h1 className="text-[22px] font-bold tracking-tight m-0">Score an agent</h1>
        </header>

        {state !== 'ready' && (
          <>
            <p className="text-[13.5px] text-v2-ink-secondary mt-1 mb-5">
              Paste an agent manifest and see where it stands. No account, and nothing is stored.
            </p>

            <div style={PANEL_STYLE}>
              <SectionLabel>Agent manifest</SectionLabel>
              <textarea
                value={manifest}
                onChange={(e) => setManifest(e.target.value)}
                spellCheck={false}
                rows={12}
                placeholder={EXAMPLE}
                aria-label="Agent manifest"
                className="w-full font-mono-v2 text-[12.5px] leading-relaxed rounded-v2-sm
                           bg-v2-surface-muted text-v2-ink border border-v2-border-subtle
                           p-3 resize-y focus:outline-none focus:border-v2-action"
              />

              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <button
                  type="button"
                  onClick={score}
                  disabled={state === 'scoring'}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-v2-sm text-[13px]
                             font-semibold bg-v2-action text-white hover:opacity-90
                             disabled:opacity-60"
                >
                  {state === 'scoring' && <Loader2 size={14} className="animate-spin" />}
                  {state === 'scoring' ? 'Scoring…' : 'Score it'}
                </button>
                {!manifest.trim() && (
                  <button
                    type="button"
                    onClick={() => setManifest(EXAMPLE)}
                    className="text-[12.5px] text-v2-ink-muted hover:text-v2-ink underline-offset-2 hover:underline"
                  >
                    use the example
                  </button>
                )}
                <span className="ml-auto text-[12px] text-v2-ink-muted">
                  Scored on the published method, version 1.2.
                </span>
              </div>

              {error && (
                <div className="mt-3 text-[12.5px]" style={{ color: 'var(--v2-danger)' }}>{error}</div>
              )}
            </div>
          </>
        )}

        {state === 'ready' && result && (
          <>
            <p className="text-[13.5px] text-v2-ink-secondary mt-1 mb-5">
              Not saved. Keep it and we will hold it at a permanent address you can return to.
            </p>

            <div style={PANEL_STYLE}>
              <div className="flex items-start gap-5 flex-wrap">
                <Dial score={result.score} style={style} />
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold tracking-wide uppercase"
                          style={{ color: 'var(--v2-text-muted)' }}>
                      {result.basisLabel}
                    </span>
                    {evidenceLabel(result.rung) && (
                      <span className="text-[11px]" style={{ color: 'var(--v2-text-muted)' }}>
                        · {evidenceLabel(result.rung)}
                      </span>
                    )}
                    {result.confidence != null && (
                      <span className="text-[11px]" style={{ color: 'var(--v2-text-muted)' }}>
                        · confidence {Number(result.confidence).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-v2-ink-secondary mt-2 mb-0">
                    Describes what this agent is able to do — what it can reach, what protects it and
                    what it is built from. It does not describe how it has behaved, because nothing
                    has been observed yet.
                  </p>
                </div>
              </div>

              {deductions.length > 0 && (
                <div className="mt-5">
                  <SectionLabel>{deductionLabel}</SectionLabel>
                  {deductions.map((d) => (
                    <DeductionRow key={d.factorKey} name={factorName(d.factorKey)} points={d.points} />
                  ))}
                </div>
              )}

              {factors.length > 0 && (
                <div className="mt-5">
                  <SectionLabel>Factors</SectionLabel>
                  {factors.map((f) => <FactorRow key={f.factorKey} factor={f} nameOf={factorName} />)}
                </div>
              )}

              {/* The coverage sentence is the argument, not a disclaimer. It says exactly how much
                  of the method a declaration could answer, and what changes if they connect. */}
              {result.inputsRead != null && result.inputsDefined != null && (
                <p className="text-[12px] text-v2-ink-muted mt-5 mb-0 leading-relaxed">
                  Confidence {Number(result.confidence).toFixed(2)} — {result.inputsRead} of{' '}
                  {result.inputsDefined} inputs. This is what you told us, not what we observed.
                  Connect the agent and the same score is computed from evidence instead.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 mt-5 flex-wrap">
              <button
                type="button"
                /* ?view=register, not ?mode= — the login page reads `view`. Carries returnTo so
                   they come back here rather than landing on a workspace home that means nothing
                   to them yet. */
                onClick={() => navigate('/login?view=register&returnTo=%2Fscore')}
                className="px-4 py-2 rounded-v2-sm text-[13px] font-semibold bg-v2-action text-white hover:opacity-90"
              >
                Keep this score
              </button>
              <button
                type="button"
                onClick={again}
                className="px-4 py-2 rounded-v2-sm text-[13px] font-medium border border-v2-border-subtle
                           text-v2-ink-secondary hover:text-v2-ink hover:bg-v2-surface-muted"
              >
                Score another
              </button>
              <span className="text-[12px] text-v2-ink-muted">
                Private until you choose otherwise.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ScoreAnAgentPage;
